import { useCallback, useState, useRef } from "react";
import { fetchPortStatusCached } from "../../../api";
import useSmartPolling from "../../../hooks/useSmartPolling";

/**
 * Smart polling after port activation.
 * maxDuration 150s so we outlast worst-case backend confirmation; initialDelay 2s for fast SMS confirmations.
 *
 * @param {Object} config
 * @param {number} config.portId - Port ID
 * @param {string|number} config.gatewayId - Gateway ID (e.g. "gw-1" or 1)
 * @param {string} [config.portNumber] - Port number string (e.g. "1.01") to match in status response
 * @param {Function} config.onStatusChange - (data) when status changes
 * @param {Function} config.onComplete - (result) when polling finishes
 */
export function useSmartActivationPolling({
  portId,
  gatewayId,
  portNumber = null,
  onStatusChange,
  onComplete,
} = {}) {
  const [isPolling, setIsPolling] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const startTimeRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const completedRef = useRef(false);
  const currentStatusRef = useRef(null);
  const attemptsRef = useRef(0);
  const smartPollingRef = useRef(null);

  // How long to poll before giving up (ms). 150s to outlast worst-case backend confirmation under load.
  const MAX_POLL_DURATION_MS = 150000;

  const fetchStatus = useCallback(async () => {
    if (!gatewayId) throw new Error("gatewayId required");
    const data = await fetchPortStatusCached(gatewayId);
    if (!data || !data.status) throw new Error("No status data returned");

    // Match by port_number string (e.g. "1.01") since API returns { port: "1.01", active, ... }
    const portStatus = portNumber
      ? data.status.find((s) => String(s.port || "").trim() === String(portNumber).trim())
      : data.status.find((s) => s.port_id === portId);

    if (!portStatus) {
      return { status: "unknown", active: 0 };
    }

    const active = portStatus.active === 1 || portStatus.active === "1";
    return {
      status: active ? "active" : portStatus.st === 3 || portStatus.st === 4 ? "registered" : "inactive",
      active: active ? 1 : 0,
      signal: portStatus.sig,
      operator: portStatus.opr,
      raw: portStatus,
    };
  }, [gatewayId, portId, portNumber]);

  const isStatusActive = useCallback((d) => d?.active === 1 || d?.status === "active", []);

  const updateRemainingTime = useCallback(() => {
    if (!startTimeRef.current) return 0;
    const elapsed = Date.now() - startTimeRef.current;
    const remaining = Math.max(0, Math.ceil((MAX_POLL_DURATION_MS - elapsed) / 1000));
    setRemainingTime(remaining);
    return remaining;
  }, [MAX_POLL_DURATION_MS]);

  const stopPollingInternal = useCallback(() => {
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current);
      timeIntervalRef.current = null;
    }
    setIsPolling(false);
    if (!completedRef.current && onComplete) {
      completedRef.current = true;
      onComplete({ status: currentStatusRef.current, attempts: attemptsRef.current });
    }
  }, [onComplete]);

  const smartPolling = useSmartPolling({
    resourceId: `port-${portId}-activation`,
    fetchFn: fetchStatus,
    onSuccess: (data, metadata) => {
      attemptsRef.current = metadata.attempt;
      currentStatusRef.current = data.status;
      setAttempts(metadata.attempt);
      setCurrentStatus(data.status);
      if (onStatusChange) onStatusChange(data);
      updateRemainingTime();
    },
    onError: (err) => console.error("[Activation Poll] Fetch error:", err),
    successCondition: isStatusActive,
    onStop: stopPollingInternal,
    initialDelay: 2000,
    maxDuration: MAX_POLL_DURATION_MS,
    enabled: !!gatewayId,
    autoStart: false,
  });

  smartPollingRef.current = smartPolling;

  const startPolling = useCallback(() => {
    if (completedRef.current) return;
    startTimeRef.current = Date.now();
    setIsPolling(true);
    setAttempts(0);
    setRemainingTime(Math.ceil(MAX_POLL_DURATION_MS / 1000));
    smartPolling.start();
    timeIntervalRef.current = setInterval(() => {
      const r = updateRemainingTime();
      if (r <= 0 && timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    }, 1000);
  }, [smartPolling, updateRemainingTime, MAX_POLL_DURATION_MS]);

  const stopPolling = useCallback(() => {
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current);
      timeIntervalRef.current = null;
    }
    smartPollingRef.current?.stop();
    setIsPolling(false);
    if (!completedRef.current && onComplete) {
      completedRef.current = true;
      onComplete({ status: currentStatusRef.current, attempts: attemptsRef.current });
    }
  }, [onComplete]);

  return {
    isPolling,
    currentStatus,
    remainingTime,
    attempts,
    start: startPolling,
    stop: stopPolling,
    lastResult: smartPolling.lastResult,
  };
}

export default useSmartActivationPolling;
